import { readBoolean } from '@/lib/prop-utils';

export interface IndexFilterOption {
  id?: string;
  name?: string;
  usageCount?: number;
}

export type IndexFilterOrdering = 'alphabetical' | 'most_used';

export type IndexFilterMode = 'tabs' | 'vertical' | 'center';

export type IndexSectionViewVariant = 'list' | 'grid' | 'split';

export function readIndexSectionViewVariant(variant: string): IndexSectionViewVariant {
  if (variant === 'list' || variant === 'grid' || variant === 'split') return variant;

  throw new Error(`Unsupported index section variant: ${variant}`);
}

export function hasVisibleFilterItems(items: IndexFilterOption[]) {
  return items.some((item) => item.name?.trim());
}

export type IndexContentMaxWidth = 'container' | '6xl' | '5xl' | '4xl';

export interface IndexLayoutProps {
  fullWidth: boolean;
  contentMaxWidth?: IndexContentMaxWidth;
}

const INDEX_SECTION_BACKGROUND_PROP_KEYS = [
  'backgroundColor',
  'backgroundFullWidth',
  'backgroundType',
  'backgroundImage',
  'backgroundFocusX',
  'backgroundFocusY',
] as const;

export function pickIndexSectionBackgroundProps(
  props: Record<string, unknown>,
): Record<string, unknown> {
  return Object.fromEntries(
    INDEX_SECTION_BACKGROUND_PROP_KEYS.flatMap((key) =>
      props[key] === undefined ? [] : [[key, props[key]]],
    ),
  );
}

export function filterHref(url: string, key: string, value: string | null) {
  const [basePath, queryString] = url.split('?');
  const params = new URLSearchParams(queryString || '');

  params.delete('page');
  params.delete(`filters[${key}]`);
  params.delete(key);

  if (value) {
    params.set(`filters[${key}]`, value);
  }

  const nextQuery = params.toString();
  return nextQuery ? `${basePath}?${nextQuery}` : basePath;
}

function readUrlSearchParams(url: string) {
  const [, queryString] = url.split('?');
  return new URLSearchParams(queryString || '');
}

function readFilterParam(url: string, key: string) {
  const params = readUrlSearchParams(url);
  return params.get(key) || params.get(`filters[${key}]`) || '';
}

export function activeFilter(url: string, key: string) {
  return readFilterParam(url, key).toLowerCase();
}

export function readIndexLayoutProps(props: Record<string, unknown>): IndexLayoutProps {
  return {
    fullWidth: readBoolean(props.backgroundFullWidth) ?? false,
  };
}

export function readConstrainedIndexLayoutProps(props: Record<string, unknown>): IndexLayoutProps {
  return {
    ...readIndexLayoutProps(props),
    contentMaxWidth: '5xl',
  };
}

function filterOptionUsageCount(item: IndexFilterOption) {
  const count = item.usageCount;
  return typeof count === 'number' && Number.isFinite(count) ? count : undefined;
}

export function sortIndexFilterOptions({
  items,
  ordering,
}: {
  items: IndexFilterOption[];
  ordering?: IndexFilterOrdering;
}) {
  const normalizedOrdering = ordering === 'most_used' ? 'most_used' : 'alphabetical';
  const hasStableUsageCounts =
    normalizedOrdering === 'most_used' &&
    items.some((item) => filterOptionUsageCount(item) != null);

  return [...items].sort((left, right) => {
    const leftName = left.name?.trim() ?? '';
    const rightName = right.name?.trim() ?? '';

    if (hasStableUsageCounts) {
      const usageDifference =
        (filterOptionUsageCount(right) ?? 0) - (filterOptionUsageCount(left) ?? 0);
      if (usageDifference !== 0) return usageDifference;
    }

    return leftName.localeCompare(rightName, undefined, { sensitivity: 'base' });
  });
}
